export type ActivityState = 'ouvert' | 'a-venir' | 'complet'

export interface Activity {
  id: string
  name: string
  audience: string
  place: string
  schedule: string
  price: string
  availability: string
  state: ActivityState
  opensOn?: string
  image: string
}

export interface Registration {
  id: string
  activity: string
  participant: string
  initials: string
  schedule: string
  place: string
  nextSession: string
}

export interface Facility {
  id: string
  name: string
  capacity: string
  availability: string
  price: string
  place: string
}

export interface Equipment {
  id: string
  name: string
  detail: string
  availability: string
  price: string
}

export const atnaChips = [
  'Trouver une activité',
  'Mes inscriptions',
  'Réserver un espace',
  'Louer de l’équipement',
]

/** Enfant inscrit par défaut dans le flux ATNA d'inscription. */
export const inscriptionChild = { name: 'Emma', age: '9 ans', initials: 'EB' }

export const inscriptionCategories = ['Natation', 'Soccer', 'Patinage', 'Arts plastiques', 'Musique']

export interface ActivitySlot {
  id: string
  category: string
  name: string
  day: string
  time: string
  place: string
  audience: string
  price: string
  spots: string
  /** Créneau mis en avant par ATNA (aucun conflit à l'horaire). */
  recommended?: boolean
  note?: string
}

export const availableSlots: ActivitySlot[] = [
  {
    id: 'natation-mer',
    category: 'Natation',
    name: 'Natation — Niveau 3',
    day: 'Mercredi',
    time: '16 h 30 à 17 h 15',
    place: 'Complexe aquatique de Brossard',
    audience: '8 à 10 ans',
    price: '68 $ · 10 séances',
    spots: '4 places restantes',
    recommended: true,
    note: 'Aucun conflit avec l’horaire d’Emma',
  },
  {
    id: 'natation-sam',
    category: 'Natation',
    name: 'Natation — Niveau 3',
    day: 'Samedi',
    time: '10 h à 10 h 45',
    place: 'Complexe aquatique de Brossard',
    audience: '8 à 10 ans',
    price: '68 $ · 10 séances',
    spots: '1 place restante',
  },
  {
    id: 'natation-ven',
    category: 'Natation',
    name: 'Natation — Niveau 3',
    day: 'Vendredi',
    time: '18 h à 18 h 45',
    place: 'Piscine Antoine-Brossard',
    audience: '8 à 11 ans',
    price: '68 $ · 10 séances',
    spots: '6 places restantes',
  },
  {
    id: 'soccer-mar',
    category: 'Soccer',
    name: 'Soccer récréatif',
    day: 'Mardi',
    time: '17 h 30 à 18 h 45',
    place: 'Parc Poly-Aréna',
    audience: '8 à 10 ans',
    price: '95 $ · saison',
    spots: '8 places restantes',
    recommended: true,
    note: 'À 5 minutes de votre adresse',
  },
  {
    id: 'soccer-sam',
    category: 'Soccer',
    name: 'Soccer récréatif',
    day: 'Samedi',
    time: '9 h à 10 h 15',
    place: 'Parc Radisson',
    audience: '8 à 10 ans',
    price: '95 $ · saison',
    spots: '3 places restantes',
  },
  {
    id: 'patin-jeu',
    category: 'Patinage',
    name: 'Patinage — Initiation',
    day: 'Jeudi',
    time: '17 h à 18 h',
    place: 'Aréna Michel-Normandin',
    audience: '7 à 10 ans',
    price: '72 $ · 12 séances',
    spots: '5 places restantes',
    recommended: true,
    note: 'Équipement fourni par la Ville',
  },
  {
    id: 'arts-mer',
    category: 'Arts plastiques',
    name: 'Atelier d’arts plastiques',
    day: 'Mercredi',
    time: '18 h à 19 h 15',
    place: 'Centre socioculturel Alphonse-Lepage',
    audience: '8 à 12 ans',
    price: '58 $ · 8 ateliers',
    spots: '7 places restantes',
    recommended: true,
    note: 'Matériel inclus',
  },
  {
    id: 'musique-lun',
    category: 'Musique',
    name: 'Éveil musical — guitare',
    day: 'Lundi',
    time: '17 h 15 à 18 h',
    place: 'Centre multigénérationnel',
    audience: '8 à 12 ans',
    price: '110 $ · 10 cours',
    spots: '2 places restantes',
    recommended: true,
    note: 'Instrument prêté pour la session',
  },
]

export const activities: Activity[] = [
  {
    id: 'natation-junior',
    name: 'Natation — Junior',
    audience: '8 à 12 ans',
    place: 'Complexe aquatique de Brossard',
    schedule: 'Samedi · 10 h',
    price: '85 $',
    availability: '12 places disponibles',
    state: 'ouvert',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/09aab17e-70f7-466f-a4f5-58c6bbdb4e7c.jpg',
  },
  {
    id: 'pickleball',
    name: 'Pickleball libre',
    audience: 'Adultes',
    place: 'Parc Maupassant',
    schedule: 'Mardi et jeudi · 18 h 30',
    price: '40 $ / saison',
    availability: 'Inscriptions à venir',
    state: 'a-venir',
    opensOn: '8 septembre · 9 h',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/69ca7f97-304e-4e63-968b-9130fae01a37.jpg',
  },
  {
    id: 'mise-en-forme',
    name: 'Mise en forme 50 ans et plus',
    audience: '50 ans et plus',
    place: 'Centre multigénérationnel de Brossard',
    schedule: 'Lundi et mercredi · 9 h 30',
    price: '60 $',
    availability: 'Complet',
    state: 'complet',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1cf684cf-5dd0-4a3c-a0a5-5144e34e195a.jpg',
  },
  {
    id: 'atelier-creatif',
    name: 'Atelier créatif ado',
    audience: '13 à 17 ans',
    place: 'Bibliothèque de Brossard',
    schedule: 'Vendredi · 17 h',
    price: '45 $',
    availability: '5 places disponibles',
    state: 'ouvert',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/a2858b27-8254-4ffc-98f5-bf2103bc4d3d.jpg',
  },
]

export const readiness = {
  activity: 'Pickleball libre',
  opensOn: 'mardi à 9 h',
  blocking: {
    label: 'L’adresse d’Emma doit être confirmée',
    detail: 'ATNA a besoin de cette confirmation pour valider son admissibilité de résidente.',
  },
  checks: [
    { id: 'carte', label: 'Carte numérique', ok: true },
    { id: 'residence', label: 'Statut de résidence', ok: true },
    { id: 'participant', label: 'Profil du participant', ok: false },
    { id: 'paiement', label: 'Moyen de paiement', ok: true },
  ],
}

export const participants = [
  { id: 'charlotte', name: 'Charlotte', initials: 'CB', detail: 'Titulaire' },
  { id: 'emma', name: 'Emma', initials: 'EG', detail: '10 ans' },
  { id: 'leo', name: 'Léo', initials: 'LG', detail: '9 ans' },
]

export const registrations: Registration[] = [
  {
    id: 'r-natation',
    activity: 'Natation — Junior',
    participant: 'Emma',
    initials: 'EG',
    schedule: 'Samedi · 10 h',
    place: 'Complexe aquatique de Brossard',
    nextSession: 'Prochaine séance : samedi 5 septembre',
  },
  {
    id: 'r-yoga',
    activity: 'Yoga du matin',
    participant: 'Charlotte',
    initials: 'CB',
    schedule: 'Mercredi · 7 h 30',
    place: 'Centre multigénérationnel de Brossard',
    nextSession: 'Prochaine séance : mercredi 9 septembre',
  },
]

export const followUps = [
  'Le cours de natation d’Emma est déplacé au bassin B ce samedi.',
  'Les inscriptions au niveau suivant ouvrent lundi — Emma y sera admissible.',
]

export const facilities: Facility[] = [
  {
    id: 'salle-communautaire',
    name: 'Salle communautaire',
    capacity: '20 à 40 personnes',
    availability: 'Samedi 13 h à 17 h',
    price: '75 $ / 4 h',
    place: 'Centre multigénérationnel de Brossard',
  },
  {
    id: 'gymnase',
    name: 'Gymnase',
    capacity: 'Sports d’équipe',
    availability: 'Samedi 9 h à 12 h',
    price: '45 $ / h',
    place: 'École secondaire Antoine-Brossard',
  },
  {
    id: 'patinoire',
    name: 'Patinoire intérieure',
    capacity: 'Glace complète',
    availability: 'Dimanche 20 h à 21 h',
    price: '120 $ / h',
    place: 'Aréna de Brossard',
  },
]

export const equipment: Equipment[] = [
  { id: 'raquettes', name: 'Raquettes à neige', detail: '4 paires · adultes', availability: 'Dès le 15 décembre', price: '10 $ / jour' },
  { id: 'patins', name: 'Patins', detail: 'Tailles 1 à 12', availability: 'Disponible', price: 'Gratuit' },
  { id: 'communautaire', name: 'Matériel communautaire', detail: 'Tables, chapiteaux, chaises', availability: 'Sur réservation', price: 'Selon le matériel' },
]
